// Used in profile-card.component.html, profile-card-student.component.html, and login-onboard-new-user.component.html

// Angular
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { FileUploader, Headers } from "ng2-file-upload";

// Services/Types/Helpers
import { CookieService } from "#core/services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { PPUser as User } from "cli-types";

// RXJS/Store
import * as fromStore from "#login/store";
import { ProfileService } from "@carnegie-learning/data-access-angular-passport";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "profile-thumbnail",
    templateUrl: "./profile-thumbnail.component.html",
    styleUrls: ["./profile-thumbnail.component.scss"],
    standalone: false
})
export class ProfileThumbnailComponent
    implements OnChanges, AfterViewInit, OnDestroy
{
    isStudent = false;

    @Input() profileData?: User;

    @ViewChild("video") video?: ElementRef;

    @ViewChild("webcamCanvas")
    webcamCanvas?: ElementRef;

    @ViewChild("photo")
    photo?: ElementRef;

    @ViewChild("fileInputCanvas")
    fileInputCanvas?: ElementRef;

    @ViewChild("fileDropzone")
    fileDropzone?: ElementRef;

    // Shared
    showProgressBar = false;
    photoSrc: any = "./assets/images/thumbnailDefault.png";
    disableUploadBtn = true;
    private ngUnsubscribe = new Subject();

    takePhotoMode = false;
    isSecureOrigin = false;
    webcamSnapshot: any;
    fileSelect = false;
    webcamLoading = false;

    // Webcam
    stream: MediaStream;
    webcamError = false;
    uploadForm = this.fb.group({
        imageFile: [""],
    });

    // File Upload
    readonly apiURL = (<any>window).apiHost + "/api/profile-image";
    authToken = this.cookieService.get("CL_TOKEN2");
    header: Headers = { name: "authorization", value: this.authToken };
    uploader: FileUploader = new FileUploader({
        allowedMimeType: [
            "image/png",
            "image/jpg",
            "image/jpeg",
            "image/gif",
            "image/bmp",
        ],
        url: this.apiURL,
        authToken: this.authToken,
        autoUpload: false,
        method: "POST",
        itemAlias: "image",
    });

    public hasBaseDropZoneOver = false;

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    constructor(
        private profileService: ProfileService,
        public store: Store<fromStore.UserState>,
        public fb: UntypedFormBuilder,
        public localStorage: LocalStorage,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private snackBarService: SnackBarService,
        private cookieService: CookieService
    ) {
        this.uploader.onAfterAddingFile = (item) => {
            item.withCredentials = false;
            this.photoSrc = this.sanitizer.bypassSecurityTrustUrl(
                window.URL.createObjectURL(item._file)
            );
            if (this.fileSelect) {
                this.showProgressBar = false;
                this.webcamSnapshot = null;
                this.cdr.detectChanges();
            }
        };

        this.uploader.onSuccessItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            if (item.isSuccess) {
                // uploaded successfully
                this.store.dispatch(
                    fromStore.userProfilePicSuccess({
                        payload: JSON.parse(response),
                    })
                );
                this.snackBarService.success("Profile Image updated!");
                this.disableUploadBtn = true;
            } else {
                // upload failed
                this.store.dispatch(fromStore.userProfilePicFail());
            }
        };
    }

    async ngAfterViewInit() {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.profileData) {
            this.isStudent = this.profileData.roleLevel === 5;
            if (this.profileData.profileUrl) {
                this.photoSrc = this.profileData.profileUrl;
            }
        }

        this.isSecureOrigin = location.protocol === "https:";
        this.webcamError = !this.isSecureOrigin;
    }

    handleImageError() {
        this.photoSrc = "./assets/images/thumbnailDefault.png";
    }

    enableWebcam() {
        this.webcamSnapshot = null;
        this.takePhotoMode = true;
        this.webcamLoading = true;
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            // webcam available
            navigator.mediaDevices
                .getUserMedia({ video: true, audio: false })
                .then((stream) => {
                    if (this.video) {
                        this.video.nativeElement.srcObject = stream;
                    }
                    this.store.dispatch(
                        fromStore.userProfileWebcam({ stream: stream })
                    );
                    this.stream = stream;
                    this.webcamLoading = false;

                    this.cdr.detectChanges();
                })
                .catch((error) => {
                    this.webcamError = true;
                    this.webcamLoading = false;
                    this.takePhotoMode = false;
                    this.cdr.detectChanges();
                });
        } else {
            this.webcamError = true;
            this.webcamLoading = false;
            this.takePhotoMode = false;
        }
    }

    ngOnDestroy() {
        this.store
            .pipe(select(fromStore.getWebCamStream))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((stream) => {
                if (stream) {
                    this.profileService.turnOffWebcam(stream);
                }
            });

        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    // Webcam
    capture() {
        this.fileSelect = false;
        this.takePhotoMode = false;

        if (this.video) {
            // copy stream to canvas as a 2d image
            const context = this.webcamCanvas?.nativeElement
                .getContext("2d")
                .drawImage(this.video.nativeElement, 0, 0, 365, 274);
            const base64Img =
                this.webcamCanvas?.nativeElement.toDataURL("image/png");
            this.uploadForm.get("imageFile")?.setValue(base64Img);
            this.webcamSnapshot = base64Img;
            this.disableUploadBtn = false;
            this.stream.getVideoTracks().forEach((track) => track.stop());

            const file = this.dataURLtoFile(base64Img, "profileImage.png");
            this.uploader.clearQueue();
            this.uploader.addToQueue([file]);
        }
    }

    // Webcam - convert base64 dataURL to readable file for file uploader
    // original solution https://stackoverflow.com/questions/16968945/convert-base64-png-data-to-javascript-file-objects
    dataURLtoFile(dataurl: any, filename: string) {
        const arr = dataurl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    preselectFile() {
        this.showProgressBar = false;
        this.uploader.clearQueue();
        this.fileSelect = true;
    }

    // File Input
    fileSelected() {
        this.showProgressBar = false;
        this.disableUploadBtn = false;
    }

    uploadFile() {
        this.store.dispatch(fromStore.userProfilePicAttempt());
        // This should only happen on account creation to properly authenticate users.
        if (!this.uploader.authToken && this.cookieService.get("CL_TOKEN2")) {
            this.uploader.authToken = this.cookieService.get("CL_TOKEN2");
        }
        this.uploader.queue[0].upload();
        this.showProgressBar = true;
    }
}